import React, { useEffect, useState } from 'react';
import { Stack, Box, Select, FormControl, InputLabel, MenuItem, Typography, Link, Button } from '@mui/material';
import { getRewardHistory, getRewardCSV } from '../api/bot.api';
import { displayWithNonZeroDecimals, formatDateTime, shortenAddress } from '../libs/utils';
import CopyButton from './CopyButton';
import { RewardStatus, TxStatus } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

export interface RewardHistoryInterface {
    _id: string;
    date: string;
    rewardVersion: number;
    sender: string;
    receiver: string;
    rewardToken: string;
    totalRewardAmount: number;
    holdingAmount: number;
    holdingPercent: number;
    rewardAmount: number;
    txId: string;
    txStatus: string;
}


export default function RewardHistoryTable() {
    const rewardSettings = useSelector((state: RootState) => state.rewardSettings);
    const [rewardVersions, setRewardVersions] = useState<number[]>([]);
    const [rewardVersion, setRewardVersion] = useState<number>(rewardSettings.rewardVersion);
    const [histories, setHistories] = useState<RewardHistoryInterface[]>([]);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    useEffect(() => {
        if(!rewardSettings._id || !rewardSettings.rewardVersion || rewardSettings.rewardVersion === 0){
            setRewardVersions([]);
            return;
        }
        const newRewardVersions = [];
        for (let i = rewardSettings.rewardVersion; i >= Math.max(rewardSettings.rewardVersion - 5, 1); i--) {
            newRewardVersions.push(i);
        }
        setRewardVersions(newRewardVersions);
        if(rewardVersion === 0 || rewardVersion === rewardSettings.rewardVersion - 1){
            setRewardVersion(rewardSettings.rewardVersion);
        }
    }, [rewardSettings.rewardVersion]);

    useEffect(() => {
        if(rewardSettings.status === RewardStatus.Running){
            if(rewardSettings.rewardVersion === rewardVersion){
                const intervalId = setInterval(() => {
                    getHistoriesHandler(rewardVersion);
                }, 5000);
                return () => clearInterval(intervalId);
            }
        }else{
            getHistoriesHandler(rewardVersion);
        }
    }, [rewardSettings.status, rewardVersion]);

    const getHistoriesHandler = async (rewardVersion: number) => {
        if(rewardVersion === 0){
            return;
        }
        const res = await getRewardHistory({rewardVersion});
        if(res.success){
            setHistories(res.data);
        }
    }

    const handleCSVDownload = async () => {
        try {
            setIsDownloading(true);
            const blob = await getRewardCSV({});
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `reward_${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading CSV:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto', flex: 1,
            '& table': {
                border: '1px solid blue',
                borderColor: 'divider',
                borderRadius: '12px 12px 0 0',
                borderBottom: 'none',
                thead: {
                    height: '50px',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#1a1a1a',
                    borderRadius: '12px',
                    zIndex: 1,
                },
                th: {
                    color: 'text.disabled',  
                },
                tr: {
                    height: '50px',
                },
                td: {
                    textAlign: 'center',
                    borderBottom: '1px solid blue',
                    borderBottomColor: 'divider',
                },
                '& .hide-xs': {
                    display: { xs: 'none', md: 'table-cell' }
                }
            },
            }}>
            <Box my={1} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                {rewardSettings.rewardVersion > 0 && rewardVersions.length > 0 ? 
                <FormControl sx={{minWidth: 120}}>
                    <InputLabel id="reward-version-select-label">Reward Version</InputLabel>
                    <Select
                        labelId="reward-version-select-label"
                        id="reward-version-select"
                        value={rewardVersion}
                        label="Reward Version"
                        onChange={(e) => setRewardVersion(e.target.value as number)}
                        size="small"
                    >
                        {rewardVersions.map((version) => (
                            <MenuItem key={version} value={version}>
                                {version}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>:
                <Typography variant="subtitle1" color="text.secondary" sx={{mb: 2}}>No reward history</Typography>
                }
                <Button 
                    variant="outlined" 
                    color="primary" 
                    sx={{height: 40, width: 170}}
                    onClick={handleCSVDownload}
                    loading={isDownloading}
                    loadingIndicator="Building..."
                >
                    Backup to CSV
                </Button>
            </Box>
            <table>
                <thead>
                    <tr>
                        <th className='hide-xs'>No.</th>
                        <th className='hide-xs'>Date</th>
                        <th>Receiver</th>
                        <th>SMRTY Amount</th>
                        <th>Reward Amount</th>
                        <th className='hide-xs'>Tx ID</th>
                        <th>Tx Status</th>
                    </tr>
                </thead>
                <tbody>
                    {histories.map((info, index) => (
                        <tr key={info._id}>
                            <td className='hide-xs'>{index + 1}</td>
                            <td className='hide-xs'>{formatDateTime(info.date)}</td>
                            <td>
                                <Stack direction="row" alignItems="center" justifyContent="center" gap={0}>
                                    {shortenAddress(info.receiver, 4)}
                                    <CopyButton text={info.receiver}/>
                                </Stack>
                            </td>
                            <td>
                                {info.holdingAmount > 0 ? <Stack direction="column" alignItems="center" justifyContent="center">
                                    {displayWithNonZeroDecimals(info.holdingAmount)}
                                    <Typography variant="body2" color="divider">
                                        {displayWithNonZeroDecimals(info.holdingPercent, 2)}%
                                    </Typography>
                                </Stack>: "_"}
                            </td>
                            <td>
                                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                                    {displayWithNonZeroDecimals(info.rewardAmount)}
                                </Stack>
                            </td>
                            <td className='hide-xs'>
                                {info.txId && <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                                    <Link 
                                        href={`https://solscan.io/tx/${info.txId}?cluster=devnet`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        underline="hover"
                                    >
                                        {shortenAddress(info.txId, 4)}
                                    </Link>
                                    <CopyButton text={info.txId}/>
                                </Stack>}
                            </td>
                            <td>
                                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                                    <Typography 
                                        variant="body2" 
                                        color={
                                            info.txStatus === TxStatus.Created ? 'text.primary' :
                                            info.txStatus === TxStatus.Waiting ? 'warning.main' :
                                            info.txStatus === TxStatus.Done ? 'primary.main' :
                                            info.txStatus === TxStatus.Failed ? 'error.main' :
                                            'text.secondary'
                                        }
                                    >
                                        {info.txStatus}
                                    </Typography>
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
            
    );
}